import React, { FunctionComponent, useState } from "react";
import { useNavigate } from "react-router";
import { Container, Table } from "react-bootstrap";
import * as _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt, faSortAlphaDown, faSortAlphaUp, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import ExportAsCsvButton from "./ExportAsCsvButton";
import ButtonRow from "../common/button/ButtonRow";
import { DeleteButton, PrimaryButton } from "../common/button/Buttons";
import { useRegistrations } from "../../hooks/useRegistrations";
import { useSchemaProperties } from "../../hooks/useSchemaProperties";
import { useLimits } from "../../hooks/useLimits";
import FirebaseService from "../../actions/firebase/FirebaseActions";
import Registration from "../../models/registration/Registration";
import { useAuthentication } from "../../hooks/useAuthentication";
import useSubscriptionType from "../../hooks/useSubscriptionType";
import { ContentLayout } from "../common/Layout";
import "dayjs/locale/de";
import { getExportRegistrations, getFormattedRegistrations } from "../../helper/getFormattedRegistrations";
import { trackRegistrationEvent, trackFeatureUsage } from "../../helper/analytics";
import { useUserIdParam } from "../../hooks/useUserIdParam";
import "./Registrations.scss";

dayjs.locale("de");

interface Props {
  isAuthenticated: boolean;
}

const Registrations: FunctionComponent<Props> = ({ isAuthenticated }) => {
  type SortDirection = "ASCENDING" | "DESCENDING" | "UNSORTED";
  const [sortProperty, setSortProperty] = useState("");
  const [sortDirection, setSortDirection] = useState<SortDirection>("UNSORTED");
  const [buttonsDisabled, disableButtons] = useState<boolean>(false);

  const userId = useUserIdParam();
  const navigate = useNavigate();
  useAuthentication(isAuthenticated);

  const [registrations, registrationsLoading, registrationsError] = useRegistrations(userId);
  const [limits, limitsLoading, limitsError] = useLimits(userId);
  const [schemaProperties, schemaPropertiesLoading, schemaPropertiesError] = useSchemaProperties(userId);
  const subscriptionType = useSubscriptionType(userId);

  const isLoading = registrationsLoading || schemaPropertiesLoading || limitsLoading;
  const isError = registrationsError || schemaPropertiesError || limitsError;

  const renderSortIcon = (property: string): React.ReactNode => {
    if (!property || sortProperty !== property) {
      return <></>;
    }
    if (sortDirection === "ASCENDING") {
      return <FontAwesomeIcon style={{ marginLeft: 20, color: "#1991ac" }} icon={faSortAlphaDown} />;
    } else if (sortDirection === "DESCENDING") {
      return <FontAwesomeIcon style={{ marginLeft: 20, color: "#1991ac" }} icon={faSortAlphaUp} />;
    } else {
      return <></>;
    }
  };

  const handleHeaderClick = (property: string): void => {
    setSortProperty(property);
    if (property === sortProperty) {
      if (sortDirection === "ASCENDING") {
        setSortDirection("DESCENDING");
      } else if (sortDirection === "DESCENDING") {
        setSortDirection("UNSORTED");
      } else if (sortDirection === "UNSORTED") {
        setSortDirection("ASCENDING");
      } else {
        setSortDirection("UNSORTED");
      }
    } else {
      setSortDirection("ASCENDING");
    }
  };

  const sortRegistrations = (sortedRegistrations: Registration[]): void => {
    if (sortProperty !== "") {
      if (sortDirection === "ASCENDING") {
        sortedRegistrations.sort((a, b) => {
          if (a.formData[sortProperty] < b.formData[sortProperty]) return -1;
          if (a.formData[sortProperty] > b.formData[sortProperty]) return 1;
          return 0;
        });
      } else if (sortDirection === "DESCENDING") {
        sortedRegistrations.sort((a, b) => {
          if (a.formData[sortProperty] > b.formData[sortProperty]) return -1;
          if (a.formData[sortProperty] < b.formData[sortProperty]) return 1;
          return 0;
        });
      } else {
        setSortProperty("");
      }
    }
  };

  if (isError) return <p>Fehler beim Laden der Daten</p>;
  else {
    const limitProperties = limits?.map((limit) => limit.property);
    const sortedRegistrations = _.cloneDeep(registrations);

    sortRegistrations(sortedRegistrations);
    const formattedRegistration = getFormattedRegistrations(sortedRegistrations);
    const registrationsForExport = getExportRegistrations(formattedRegistration, limits);

    return (
      <ContentLayout
        isLoading={isLoading}
        title={"Anmeldungen"}
        siteTitle={"Spielgruppe Online - Anmeldungen"}
        fullWith={true}>
        <Container>
          <ButtonRow>
            <PrimaryButton
              className={"me-1 mt-1 mt-md-0"}
              disabled={buttonsDisabled}
              onClick={() => {
                trackFeatureUsage("create_registration_button");
                navigate(`/user/${userId}/registrations/create`);
              }}>
              Neue Anmeldung
            </PrimaryButton>
            <ExportAsCsvButton
              className={"me-1 mt-1 mt-md-0"}
              properties={schemaProperties}
              registrations={registrationsForExport}
              limits={limits}
              subscriptionType={subscriptionType}
            />
            <DeleteButton
              disabled={buttonsDisabled}
              className={"mt-1 mt-md-0"}
              onClick={() => {
                disableButtons(true);
                if (
                  window.confirm(
                    "Wir empfehlen Ihnen, vor dem Löschen alle Anmeldungen zu exportieren. " +
                      "Wenn Sie fortfahren werden alle Anmeldungen unwiderruflich gelöscht. " +
                      "Möchten Sie fortfahren?"
                  )
                ) {
                  const registrationCount = registrations?.length || 0;
                  trackRegistrationEvent("bulk_delete_initiated", { count: registrationCount });

                  FirebaseService.deleteAllRegistrations(userId)
                    .then(() => {
                      trackRegistrationEvent("bulk_delete_completed", { count: registrationCount });
                      toast.success("Alle Anmeldungen wurden erfolgreich gelöscht");
                    })
                    .catch(() => {
                      trackRegistrationEvent("bulk_delete_failed", { count: registrationCount });
                      toast.error(
                        "Fehler beim Löschen der Anmeldungen aufgetreten. Bitte versuchen Sie es später erneut."
                      );
                    })
                    .finally(() => disableButtons(false));
                } else {
                  trackRegistrationEvent("bulk_delete_cancelled");
                }
              }}>
              Alle Anmeldungen löschen
            </DeleteButton>
          </ButtonRow>
        </Container>

        <div className="registrations-table-wrapper">
          <Table striped bordered hover size={"xl"} className={"registrations-table sticky-table"}>
            <thead>
              <tr>
                {schemaProperties.map((property) => {
                  return (
                    <th key={property} onClick={() => handleHeaderClick(property)}>
                      {property} {renderSortIcon(property)}
                    </th>
                  );
                })}
                <th>Gewählte Angebote</th>
                <th>Gesamtpreis</th>
                <th
                  onClick={() => {
                    setSortProperty("");
                  }}>
                  Zeitpunkt
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {formattedRegistration.map((registration, index) => {
                return (
                  <tr key={index}>
                    {schemaProperties.map((property) => (
                      <td key={property}>{registration.formData[property]}</td>
                    ))}
                    <td>
                      <p className={"mb-0"}>
                        {limitProperties
                          ?.map((property) => (registration.formData[property] ? property : undefined))
                          .filter((property) => property !== undefined)
                          .map((property) => (
                            <span key={property}>
                              {property} <br />
                            </span>
                          ))}
                      </p>
                    </td>
                    <td>
                      CHF{" "}
                      {limits
                        .map((limit) => (registration.formData[limit.property] && limit.price ? limit.price : 0))
                        .reduce((total, currentValue) => total + currentValue, 0)}
                    </td>
                    <td>
                      {registration.creation.createdAt.toLocaleDateString()}
                      {"  "}
                      {registration.creation.createdAt.toLocaleTimeString()}
                    </td>
                    <td>
                      <PrimaryButton
                        disabled={buttonsDisabled}
                        className={"me-1"}
                        size={"sm"}
                        onClick={() => {
                          trackFeatureUsage("edit_registration", { registration_id: registration.id });
                          navigate(`/user/${userId}/registrations/${registration.id}`);
                        }}>
                        <FontAwesomeIcon icon={faPencilAlt} />
                      </PrimaryButton>

                      <DeleteButton
                        disabled={buttonsDisabled}
                        size={"sm"}
                        onClick={() => {
                          if (window.confirm("Anmeldung wirklich löschen?")) {
                            disableButtons(true);
                            trackRegistrationEvent("delete_initiated", { registration_id: registration.id });

                            FirebaseService.deleteRegistration(userId, registration.id)
                              .then(() => {
                                trackRegistrationEvent("delete_completed", { registration_id: registration.id });
                              })
                              .catch(() => {
                                trackRegistrationEvent("delete_failed", { registration_id: registration.id });
                                toast.error(
                                  "Die Anmeldung konnte nicht gelöscht werden. Bitte versuchen Sie es später nochmals."
                                );
                              })
                              .finally(() => disableButtons(false));
                          } else {
                            trackRegistrationEvent("delete_cancelled", { registration_id: registration.id });
                          }
                        }}>
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </DeleteButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </ContentLayout>
    );
  }
};

export default Registrations;
